import styles from './partner-discounts.module.css'
import mixpanel from '@disco/api/mixpanel'
import React, { Fragment, useEffect, useRef, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import type { SwiperRef } from 'swiper/react'
import logoA2IM from 'assets/images/partner-discounts/logo-a2im.jpg'
import logoGroover from 'assets/images/partner-discounts/logo-groover.jpg'
import logoNina from 'assets/images/partner-discounts/logo-nina.jpg'
import logoSubmitHub from 'assets/images/partner-discounts/logo-submit-hub.jpg'
import logoUnhurd from 'assets/images/partner-discounts/logo-unhurd.png'
import logoEven from 'assets/images/partner-discounts/logo-even.jpg'
import logoTracklib from 'assets/images/partner-discounts/logo-tracklib.jpg'
import logoAbleton from 'assets/images/partner-discounts/logo-ableton.jpg'
import logoSubvert from 'assets/images/partner-discounts/logo-subvert.jpg'

import Button from 'components/ui/button'

const PartnerDiscountItem = ({
  title,
  description,
  imageUrl,
  imageAltText,
  externalLinkUrl,
  redeemLabel = 'Redeem',
  onClick,
}: {
  title: string
  description: React.ReactNode
  imageUrl: string
  imageAltText: string
  externalLinkUrl: string
  redeemLabel?: string
  onClick?: () => void
}): React.ReactElement<'div'> => (
  <div className={styles.partnerDiscountItemContainer}>
    <div className={styles.partnerDiscountItemHead}>
      <img
        src={imageUrl}
        alt={imageAltText}
        className={styles.partnerDiscountItemImage}
        width="48px"
        height="48px"
      />
      <div className={styles.partnerDiscountItemTitleContainer}>
        <div className={styles.partnerDiscountItemTitleText}>{title}</div>
        <a
          target="_blank"
          rel="noreferrer"
          href={externalLinkUrl}
          className={styles.redeemLink}
          onClick={onClick}
        >
          {redeemLabel}
        </a>
      </div>
    </div>
    <div className={styles.partnerDiscountItemDescriptionText}>
      <div>{description}</div>
    </div>
  </div>
)

const availablePartnerDiscounts = [
  {
    id: 'subvert',
    title: 'Release on Subvert',
    description:
      'Subvert is a cooperative platform for independent artists and labels to share music, sell directly to fans, and build sustainable careers. By putting ownership back in the hands of creators, Subvert helps artists grow their audiences while listeners discover and support new music through community-driven discovery.',
    imageUrl: logoSubvert,
    imageAltText: 'subvert logo',
    externalLinkUrl: 'https://www.subvert.fm/',
    redeemLabel: 'Try',
  },
  {
    id: 'ableton',
    title: '20% off all Ableton products',
    description: (
      <Fragment>
        As a valued DISCO user, you can now enjoy 20% off Ableton software and
        hardware.
        <br />
        <br />
        This special collaboration is designed to empower creators like you by
        combining the intuitive workflow of Ableton Live, Push, and Move with
        DISCO’s seamless file-sharing and collaboration platform. Use code{' '}
        <strong>DISCO20</strong> at checkout.
      </Fragment>
    ),
    imageUrl: logoAbleton,
    imageAltText: 'ableton Logo',
    externalLinkUrl: 'https://www.ableton.com/',
    redeemLabel: 'Redeem',
  },
  {
    id: 'tracklib',
    title: '$1 first month on Tracklib',
    description:
      'Get one month’s full access to Tracklib for $1, giving you access to a wide array of material made available to sample: songs, stems, acapellas, packs, loops and one-shots',
    imageUrl: logoTracklib,
    imageAltText: 'tracklib Logo',
    externalLinkUrl: 'https://tracklib.com/disco',
    redeemLabel: 'Redeem',
  },
  {
    id: 'unhurd',
    title: 'Get 50% off a Pro membership',
    description:
      "un:hurd music is your complete, all-in-one marketing and promotion platform. From pitching to your most relevant Spotify playlists, launching automated ads on TikTok or completing a step-by-step release plan for your upcoming release, we've got you covered. Join over 100,000 artists who are levelling up their marketing game with un:hurd music.",
    imageUrl: logoUnhurd,
    imageAltText: 'un:hurd logo',
    externalLinkUrl:
      'https://app.unhurdmusic.com/?ref=disco&utm_source=disco&utm_medium=referral&utm_campaign=partnership',
    redeemLabel: 'Redeem',
  },
  {
    id: 'even',
    title: 'Activate your fans on EVEN',
    description:
      'EVEN is a platform to buy art directly from artists. Sell exclusive music, content, merch, and events. Fans pay what they want, and artists get paid daily.\n\nArtists on DISCO cut the line and will receive extra support from the EVEN artist relations team.',
    imageUrl: logoEven,
    imageAltText: 'even Logo',
    externalLinkUrl: 'https://backstage.even.biz/partner/disco',
    redeemLabel: 'Try',
  },
  {
    id: 'groover',
    title: 'Get 15% off Groover!',
    description:
      'Groover helps artists get their music heard. Through an innovative web platform, Groover connects artists who want to promote their music with the best curators, radios and labels seeking emerging talents.\n\nOn groover.co, artists can send their music directly to a selection of blogs, radios, playlist curators, record labels and pros of their choice, get feedback guaranteed, and coverage!',
    imageUrl: logoGroover,
    imageAltText: 'Groover logo',
    externalLinkUrl: 'https://try.groover.co/disco/',
    redeemLabel: 'Redeem',
  },
  {
    id: 'submithub',
    title: 'Get 20% off Submithub!',
    description:
      'Founded in 2015, SubmitHub makes it easy for musicians and industry professionals to connect with curators and influencers who can share their music. The platform is used daily by the marketing teams at Universal, Sony, and Warner, who favour its ease of use, transparency, and quality control.',
    imageUrl: logoSubmitHub,
    imageAltText: 'SubmitHub logo',
    externalLinkUrl: 'https://www.submithub.com/partner/disco',
    redeemLabel: 'Redeem',
  },
  {
    id: 'a2im',
    title: 'Get 25% off A2IM today!',
    description: (
      <Fragment>
        Sign up for A2IM Artist Pro through DISCO for 25% off your 1st Year of
        Membership! The independent sector’s dedicated trade organization, A2IM,
        invites you to join a community of artists taking advantage of exclusive
        professional and educational resources and services offered through A2IM
        Artist Pro.
        <br />
        <br />
        <strong>Must create an account & be logged in to access.</strong>
      </Fragment>
    ),
    imageUrl: logoA2IM,
    imageAltText: 'A2IM logo',
    externalLinkUrl:
      'https://members.a2im.org/a2im-member-resources/free-disco-trial-for-90-days?hsLang=en',
    redeemLabel: 'Redeem',
  },
]
export const PartnerDiscounts = (): React.ReactElement<'div'> => {
  const [buttonIsDisabled, setButtonIsDisabled] = useState({
    prev: true,
    next: false,
  })
  const swiperRef = useRef<SwiperRef>(null)

  const goNext = () => {
    swiperRef.current?.swiper?.slideNext()
  }

  const goPrev = () => {
    swiperRef.current?.swiper?.slidePrev()
  }

  useEffect(() => {
    if (!swiperRef.current?.swiper) {
      return
    }

    const { swiper } = swiperRef.current

    function handleIndexChange(eventName, { isBeginning, isEnd }) {
      switch (eventName) {
        case 'progress':
        case 'resize':
        case 'afterInit':
        case 'slidesUpdated': {
          setButtonIsDisabled({
            prev: isBeginning,
            next: isEnd,
          })
          break
        }
      }
    }

    swiper.onAny(handleIndexChange)
    return () => {
      swiper.offAny(handleIndexChange)
    }
  }, [])
  return (
    <div className={styles.partnerDiscountsRoot}>
      <div className={styles.subheadingContainer}>
        <h4 className={styles.subheadingText}>Partners</h4>
        <div className={styles.paginationContainer}>
          <Button
            icon="arrow-left-big-v2"
            iconStyle={{
              width: 16,
              height: 16,
            }}
            onClick={goPrev}
            disabled={buttonIsDisabled.prev}
          />
          <Button
            icon="arrow-right-big-v2"
            iconStyle={{
              width: 16,
              height: 16,
            }}
            onClick={goNext}
            disabled={buttonIsDisabled.next}
          />
        </div>
      </div>
      <Swiper ref={swiperRef} slidesPerView="auto" spaceBetween={24}>
        {availablePartnerDiscounts.map(partnerDiscount => (
          <SwiperSlide
            key={partnerDiscount.id}
            className={styles.partnerDiscountItemSlide}
          >
            <PartnerDiscountItem
              title={partnerDiscount.title}
              description={partnerDiscount.description}
              imageUrl={partnerDiscount.imageUrl}
              imageAltText={partnerDiscount.imageAltText}
              externalLinkUrl={partnerDiscount.externalLinkUrl}
              redeemLabel={partnerDiscount.redeemLabel}
              onClick={() =>
                mixpanel.sendPureEvent('Click button', {
                  Location: 'Explorer',
                  Context: 'Opportunities',
                  Campaign: `${partnerDiscount.id} ${partnerDiscount.title}`,
                  Copy: partnerDiscount.redeemLabel,
                })
              }
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  )
}
